import {
    ChangeEvent,
    InputHTMLAttributes,
    MutableRefObject,
    ReactNode,
    forwardRef,
    useEffect,
    useId,
    useRef,
    useState,
} from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import styles from "./Checkbox.module.scss";

export interface CheckboxProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
    /** Visible label rendered to the right of the checkbox icon. */
    label?: ReactNode;
    /** Toggles label rendering. Defaults to `true`. */
    showLabel?: boolean;
    /**
     * Visual + semantic indeterminate state.
     * Note: native checkbox `indeterminate` is not an attribute, it must be
     * set imperatively on the DOM node (handled internally).
     */
    indeterminate?: boolean;
    /**
     * Error visual state (red border). Kept separate from `aria-invalid`
     * so consumers can style errors from form libs while still using native
     * validity states when they want.
     */
    error?: boolean;
    /**
     * Change callback with the raw boolean checked value for convenience.
     * Fires alongside the native `onChange` if both are provided.
     */
    onCheckedChange?: (checked: boolean) => void;
}

const CheckIcon = () => <Icon icon="check" />;
const MinusIcon = () => <Icon icon="minus" />;

/**
 * Treez DS � Checkbox (Wave 1).
 *
 * Source: Figma `Checkbox` component (file `qgFv3OQga6RQ4luvSFuGFP`,
 * node `565:41544`).
 *
 * Visual specs:
 * - icon box: 20�20
 * - radius: 6
 * - stroke: 1.5px
 * - checked fill: green05 (hover: green06)
 * - unchecked stroke: grey05 (hover: grey07)
 * - focus: black stroke
 * - disabled unchecked: bg grey03 + stroke grey04
 * - disabled checked: bg grey03 + stroke grey04 + disabled check icon
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    function Checkbox(
        {
            label = "Checkbox Label",
            showLabel = true,
            checked,
            defaultChecked,
            indeterminate = false,
            disabled = false,
            error = false,
            className,
            id,
            name,
            onChange,
            onCheckedChange,
            ...rest
        },
        ref,
    ) {
        const generatedId = useId();
        const inputId = id ?? `ds-checkbox-${generatedId}`;
        const isControlled = checked !== undefined;
        const [uncontrolledChecked, setUncontrolledChecked] = useState(
            Boolean(defaultChecked),
        );
        const currentChecked = isControlled
            ? Boolean(checked)
            : uncontrolledChecked;

        const internalRef = useRef<HTMLInputElement | null>(null);

        /* Merge forwarded ref with local ref so we can set indeterminate. */
        const setRefs = (node: HTMLInputElement | null) => {
            internalRef.current = node;
            if (typeof ref === "function") {
                ref(node);
            } else if (ref) {
                (ref as MutableRefObject<HTMLInputElement | null>).current =
                    node;
            }
        };

        useEffect(() => {
            if (!internalRef.current) return;
            internalRef.current.indeterminate = Boolean(indeterminate);
        }, [indeterminate]);

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (!isControlled) {
                setUncontrolledChecked(event.target.checked);
            }
            onChange?.(event);
            onCheckedChange?.(event.target.checked);
        };

        return (
            <label
                className={cx(styles.root, className)}
                data-checked={currentChecked || undefined}
                data-disabled={disabled || undefined}
                data-error={error || undefined}
                data-indeterminate={indeterminate || undefined}
                data-ui="checkbox"
                htmlFor={inputId}
            >
                <input
                    checked={isControlled ? checked : undefined}
                    className={styles.input}
                    defaultChecked={isControlled ? undefined : defaultChecked}
                    disabled={disabled}
                    id={inputId}
                    name={name}
                    onChange={handleChange}
                    ref={setRefs}
                    type="checkbox"
                    {...rest}
                />

                <span aria-hidden className={styles.box}>
                    {indeterminate ? <MinusIcon /> : <CheckIcon />}
                </span>

                {showLabel && <span className={styles.label}>{label}</span>}
            </label>
        );
    },
);

Checkbox.displayName = "Checkbox";
